import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { supabaseClient } from "@/lib/supabase";
import { services as s } from "@/services";

interface CopyScheduleParams {
    scheduleId: string;
}

export async function copySchedule({ scheduleId }: CopyScheduleParams) {
    // First, select the schedule and its timing relationships with full timing data
    const { data: schedule, error: fetchError } = await supabaseClient
        .from("schedules")
        .select(`
            *,
            schedule_timings(
                timings(*)
            )
        `)
        .eq("schedule_id", scheduleId)
        .single();

    if (fetchError) {
        throw new Error(`Failed to fetch schedule: ${fetchError.message}`);
    }

    if (!schedule) {
        throw new Error("Schedule not found");
    }

    const { data: sessionData } = await supabaseClient.auth.getUser();
    const author = sessionData.user?.id ?? null;

    // Extract timings from the nested structure
    const timings = schedule.schedule_timings?.map((st: any) => st.timings).filter(Boolean) || [];

    // Create new schedule with copied data
    const newScheduleId = s.uuidv4();
    const { data: newSchedule, error: scheduleError } = await supabaseClient
        .from("schedules")
        .insert({
            schedule_id: newScheduleId,
            schedule_name: `${schedule.schedule_name} (Copy)`,
            schedule_description: schedule.schedule_description,
            is_published: false, // Always start as draft
            author,
        })
        .select()
        .single();

    if (scheduleError) {
        throw new Error(`Failed to create schedule: ${scheduleError.message}`);
    }

    // Create new timings (copying timing data but with new IDs)
    if (timings && timings.length > 0) {
        const newTimings = timings.map((timing: any) => ({
            timing_id: s.uuidv4(),
            label: timing.label,
            interval: timing.interval,
            time_unit: timing.time_unit,
            repeats: timing.repeats,
            is_infinite: timing.is_infinite,
            is_autoname: timing.is_autoname,
            author,
        }));

        // Insert new timings
        const { data: insertedTimings, error: timingsError } = await supabaseClient
            .from("timings")
            .insert(newTimings)
            .select();

        if (timingsError) {
            throw new Error(`Failed to create timings: ${timingsError.message}`);
        }

        // Create schedule-timing relationships with new timing IDs
        if (insertedTimings && insertedTimings.length > 0) {
            const scheduleTimings = insertedTimings.map((timing) => ({
                schedule_id: newScheduleId,
                timing_id: timing.timing_id,
            }));

            const { error: relationshipError } = await supabaseClient.from("schedule_timings").insert(scheduleTimings);

            if (relationshipError) {
                throw new Error(`Failed to create timing relationships: ${relationshipError.message}`);
            }
        }
    }

    return newSchedule;
}

export function useCopyScheduleMutation() {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: copySchedule,
        onSuccess: (data) => {
            toast.success(toastCopy.scheduleUpdatedSuccess);
            // Invalidate and refetch schedules
            queryClient.invalidateQueries({ queryKey: ["time-templates"] });
            return data;
        },
        onError: (error) => {
            toast.error(toastCopy.failedCopySchedule(error.message));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["schedule"] });
            queryClient.invalidateQueries({ queryKey: ["schedules"] });
        },
    });
}
